import ErrorPage from "next/error";
import useTranslation from "next-translate/useTranslation";
import React, { VFC } from "react";
import {
  QaArticleEvaluationCallbackData,
  QaCommentCallbackData,
} from "~/components/modules/QaReviewCommentForm/QaReviewCommentForm";
import { getQuestionShortContent } from "~/models/questionAnswer";
import { AnswerPreviewData } from "~/services/questionAnswers/ssrData/preview";
import Layout from "faqSrc/components/layout/Layout";
import { MetaTag } from "faqSrc/components/modules/metaTag";
import ReviewPresenter from "faqSrc/components/templates/questions/questionAnswer/review/ReviewPresenter";
import { createQaArticleEvaluationApi } from "faqSrc/iterators/internalApi/qaArticleEvaluations/createQaArticleEvaluationApi";
import { createQaUserReviewCommentApi } from "faqSrc/iterators/internalApi/qaReviewComments/createQaUserReviewCommentApi";
import { postQaUserReviewCommentNotificationApi } from "faqSrc/iterators/internalApi/qaReviewComments/postQaUserReviewCommentNotificationApi";

const TemplateAnswerReview: VFC<AnswerPreviewData> = (initialData) => {
  const { t } = useTranslation("question");

  return (
    <Layout project={initialData.project}>
      <>
        <MetaTag
          isNoIndexPage={true}
          title={t("meta-title.answer-preview", {
            content: getQuestionShortContent(
              initialData?.questionAnswer?.questionContent || ""
            ),
          })}
        />
        <TemplateAnswerReviewContent initialData={initialData} />
      </>
    </Layout>
  );
};

const TemplateAnswerReviewContent: VFC<{
  initialData: AnswerPreviewData;
}> = ({ initialData }) => {
  const questionAnswer = initialData.questionAnswer;
  if (!questionAnswer || !initialData.project || !initialData.topic) {
    return <ErrorPage statusCode={500} />;
  }

  const onSubmitQaReviewComment = async (
    data: QaCommentCallbackData
  ): Promise<{ error?: Error; isSuccess: boolean }> => {
    try {
      const { qaReviewCommentId } = await createQaUserReviewCommentApi({
        body: {
          content: data.content,
          line: data.line,
          targetQaField: data.targetQaField,
          targetText: data.targetText,
        },
        query: { questionAnswerId: questionAnswer.id },
      });
      if (!qaReviewCommentId) {
        throw Error("Fail to create QaReviewComment by normal user");
      }
      await postQaUserReviewCommentNotificationApi({
        questionAnswerId: questionAnswer.id,
        reviewCommentId: qaReviewCommentId,
      });
      return { isSuccess: true };
    } catch (e) {
      return { error: e, isSuccess: false };
    }
  };

  const onSubmitQaArticleEvaluation = async ({
    comment,
    evaluationType,
    line,
    targetQaField,
    targetText,
  }: QaArticleEvaluationCallbackData): Promise<{
    error?: Error;
    isSuccess: boolean;
  }> => {
    try {
      await createQaArticleEvaluationApi({
        body: {
          comment,
          evaluationType,
          line,
          targetQaField,
          targetText,
        },
        query: {
          questionAnswerId: questionAnswer.id,
        },
      });
      return { isSuccess: true };
    } catch (e) {
      return { error: e, isSuccess: false };
    }
  };

  return (
    <ReviewPresenter
      answeredUser={initialData.answeredUser}
      onSubmitQaArticleEvaluation={onSubmitQaArticleEvaluation}
      onSubmitQaReviewComment={onSubmitQaReviewComment}
      project={initialData.project}
      questionAnswer={questionAnswer}
      topic={initialData.topic}
      revisionStatus={initialData.latestQaRevision?.status}
      task={initialData.task}
    />
  );
};

export default TemplateAnswerReview;
